import { AxiosInstance } from "axios";
import { CensusCategory } from "../types/Census/CensusCategory";
import { CensusOutputAreaType } from "../types/Census/CensusOutputAreaType";
import { Layer } from "../types/Layer";
import { RouteLineImport } from "../types/RouteLine/RouteLineImport";
import { RouteLine } from "../types/RouteLine/RouteLine";
import { Group } from "../types/OsPoi/OsPoiTypes";
import { Dataset } from "../types/UsCensus/Dataset";
import PTCoverageArea from "../types/PTCoverageArea/PTCoverageArea";
import { MultiLineString } from "ol/geom";
import { InrixImport } from "../types/Inrix/InrixImport";

export default class LayerService {
  private _endpoint = "Layer";
  private _client: AxiosInstance;

  constructor(client: AxiosInstance) {
    this._client = client;
  }

  public async ListAvailable(): Promise<Layer[]> {
    return this._client.get<any>(`${this._endpoint}`).then((response) => {
      return response.data;
    });
  }

  public async GetCensusCategories(
    releaseYear: number,
    outputAreaTypeId: number,
  ): Promise<CensusCategory[]> {
    return this._client
      .get<any>(
        `${this._endpoint}/Basemap:Census/Categories?releaseYear=` +
          releaseYear +
          `&outputAreaTypeId=` +
          outputAreaTypeId,
      )
      .then((response) => {
        return response.data;
      });
  }

  public async GetCensusReleaseYears(): Promise<number[]> {
    return this._client
      .get<any>(`${this._endpoint}/Basemap:Census/ReleaseYears`)
      .then((response) => {
        return response.data;
      });
  }

  public async GetCensusOutputAreaTypes(
    releaseYear: number,
  ): Promise<CensusOutputAreaType[]> {
    return this._client
      .get<any>(
        `${this._endpoint}/Basemap:Census/OutputAreaTypes?releaseYear=` +
          releaseYear,
      )
      .then((response) => {
        return response.data;
      });
  }

  public async GetColumnValueForOutputArea(
    releaseYear: number,
    columnId: number,
    outputArea: string,
  ): Promise<number> {
    return this._client
      .get<any>(
        `${this._endpoint}/Basemap:Census/GetColumnValueForOutputArea/${releaseYear}/${columnId}/${outputArea}`,
      )
      .then((response) => {
        return response.data;
      });
  }

  public async ListAll(): Promise<Layer[]> {
    return this._client
      .get<any>(`${this._endpoint}?listAll=true`)
      .then((response) => {
        return response.data;
      });
  }

  public GetPublicTransportImportQuarters(): Promise<string[]> {
    return this._client
      .get<any>(`${this._endpoint}/Basemap:PublicTransport/ImportQuarters`)
      .then((response) => {
        return response.data;
      });
  }

  public GetPTCoverageAreaData(isTile: string): Promise<PTCoverageArea[]> {
    return this._client
      .get<any>(
        `${this._endpoint}/Basemap:PublicTransport/PTCoverageAreas?isTile=` +
          isTile,
      )
      .then((response) => {
        return response.data;
      });
  }

  public GetRouteLineImportQuarters(): Promise<RouteLineImport[]> {
    return this._client
      .get<any>(`${this._endpoint}/Basemap:RouteLine`)
      .then((response) => {
        return response.data;
      });
  }

  public GetRouteLineByIds(ids: number[]): Promise<RouteLine[]> {
    return this._client
      .post<any>(`${this._endpoint}/Basemap:RouteLine/ByIds`, ids)
      .then((response) =>
        response.data.map(
          (item: any) =>
            ({
              ...item,
              location: new MultiLineString(item.location.coordinates),
            }) as RouteLine,
        ),
      );
  }

  public GetOsPoiGroups(): Promise<Group[]> {
    return this._client
      .get<any>(`${this._endpoint}/OS:POI/Groups`)
      .then((response) => {
        return response.data;
      });
  }

  public GetUsCensusDatasets(): Promise<Dataset[]> {
    return this._client
      .get<any>(`${this._endpoint}/US:Census/Datasets`)
      .then((response) => {
        return response.data;
      });
  }

  public GetInrixImports(): Promise<InrixImport[]> {
    return this._client
      .get<any>(`${this._endpoint}/Inrix:Imports`)
      .then((response) => {
        return response.data;
      });
  }

  public GetAllLayers(): Promise<Layer[]> {
    return Promise.resolve([
      { id: 1, name: "OSM:Roads", displayName: "OSM Roads" },
      { id: 2, name: "OS:OpenRoads", displayName: "OS OpenRoads" },
      {
        id: 3,
        name: "Basemap:PublicTransport",
        displayName: "Public Transport",
      },
      { id: 4, name: "OS:MRN", displayName: "OS Multi-Modal Routing Network" },
      { id: 5, name: "OS:Highways", displayName: "OS Highways" },
      { id: 6, name: "OSM:POI", displayName: "OSM Points of Interest" },
      { id: 7, name: "Basemap:Census", displayName: "Census" },
      { id: 8, name: "Basemap:RouteLine", displayName: "RouteLine" },
      { id: 9, name: "OS:POI", displayName: "OS Points of Interest" },
      { id: 10, name: "OS:CodePoints", displayName: "OS Code Points" },
    ] as Layer[]);
  }
}
